import { BaseApi } from '@/api/BaseApi';
import {
  CellAgentResponseFromApi,
  TablePaperRecommendationResponse,
} from '@/@types/Table';
import {
  NewTableColumn,
  TableColumn,
  TableColumnId,
  TableRow,
  TableRowId,
} from '@/models/Table';
import { ThreadId } from '@/models/Thread';
import { UpdateTableWidgetResponseBody } from '@/api/ApiResponse';

const TABLE_URL_PREFIX = '/api/table';

export class TableAPI extends BaseApi {
  async getValuesForColumn(payload: {
    column_name: string;
    corpus_ids: string[];
    column_def: string;
    is_metadata?: boolean;
    current_values: string[];
  }) {
    return this.request<CellAgentResponseFromApi[]>({
      url: `${TABLE_URL_PREFIX}/column/cells/populate`,
      method: 'POST',
      body: payload,
    });
  }

  async getTablePaperRecommendations(corpusIds: number[]) {
    return this.request<TablePaperRecommendationResponse>({
      url: `${TABLE_URL_PREFIX}/paper_recommendations`,
      method: 'POST',
      body: { corpus_ids: corpusIds },
    });
  }

  async createTable(threadId: string) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/`,
      method: 'POST',
      body: {
        thread_id: threadId,
      },
    });
  }

  async addPapersToTable({
    tableId,
    threadId,
    paperCorpusIds,
  }: {
    tableId: string;
    threadId: string;
    paperCorpusIds: number[];
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/rows`,
      method: 'POST',
      body: {
        thread_id: threadId,
        paper_corpus_ids: paperCorpusIds,
      },
    });
  }

  async addColumnsToTable({
    tableId,
    threadId,
    columns,
  }: {
    tableId: string;
    threadId: string;
    columns: NewTableColumn[];
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/columns`,
      method: 'POST',
      body: {
        thread_id: threadId,
        columns: columns.map((column) => ({
          name: column.name,
          description: column.description,
          is_metadata: column.isMetadata,
        })),
      },
    });
  }

  async removeRowsFromTable({
    tableId,
    threadId,
    rowIds,
  }: {
    tableId: string;
    threadId: string;
    rowIds: string[];
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/rows`,
      method: 'DELETE',
      body: {
        thread_id: threadId,
        row_ids: rowIds,
      },
    });
  }

  async removeColumnsFromTable({
    tableId,
    threadId,
    columnIds,
  }: {
    tableId: string;
    threadId: string;
    columnIds: string[];
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/columns`,
      method: 'DELETE',
      body: {
        thread_id: threadId,
        column_ids: columnIds,
      },
    });
  }

  async updateRowInTable({
    tableId,
    threadId,
    row,
  }: {
    tableId: string;
    threadId: string;
    row: TableRow;
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/row/${row.id}`,
      method: 'POST',
      body: {
        thread_id: threadId,
        row: {
          id: row.id,
          display_value: row.displayValue,
          paper_corpus_id: row.paperCorpusId,
        },
      },
    });
  }

  async updateColumnInTable({
    tableId,
    threadId,
    column,
  }: {
    tableId: string;
    threadId: string;
    column: TableColumn;
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/column/${column.id}`,
      method: 'POST',
      body: {
        thread_id: threadId,
        column: {
          id: column.id,
          name: column.name,
          description: column.description,
          tools: column.tools,
          is_metadata: column.isMetadata,
        },
      },
    });
  }

  async editTableCell({
    tableId,
    threadId,
    cellId,
    editedValue,
    editedByUUID,
  }: {
    tableId: string;
    threadId: string;
    cellId: string;
    editedValue: string;
    editedByUUID: string;
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/cells/${cellId}`,
      method: 'POST',
      body: {
        thread_id: threadId,
        edited_value: editedValue,
        edited_by_uuid: editedByUUID,
      },
    });
  }

  async populateTableCells({
    tableId,
    threadId,
    rowIds,
    columnIds,
  }: {
    tableId: string;
    threadId: ThreadId;
    rowIds?: TableRowId[];
    columnIds?: TableColumnId[];
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/cells/populate`,
      method: 'POST',
      body: {
        thread_id: threadId,
        row_ids: rowIds ?? null,
        column_ids: columnIds ?? null,
      },
    });
  }

  async reorderTableRows({
    tableId,
    threadId,
    rows,
  }: {
    tableId: string;
    threadId: string;
    rows: TableRow[];
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/rows/order`,
      method: 'PUT',
      body: {
        thread_id: threadId,
        row_ids: rows.map((row) => row.id),
      },
    });
  }

  async reorderTableColumns({
    tableId,
    threadId,
    columns,
  }: {
    tableId: string;
    threadId: string;
    columns: TableColumn[];
  }) {
    return this.request<UpdateTableWidgetResponseBody>({
      url: `${TABLE_URL_PREFIX}/${tableId}/columns/order`,
      method: 'PUT',
      body: {
        thread_id: threadId,
        column_ids: columns.map((column) => column.id),
      },
    });
  }
}

let tableAPI: TableAPI | undefined;
export function getTableAPI(): TableAPI {
  if (!tableAPI) {
    tableAPI = new TableAPI();
  }
  return tableAPI;
}
